<script>
  import * as d3 from 'd3';
  // eslint-disable-next-line
  import { onMount } from 'svelte';
  import Versor from './util/versor';

  // eslint-disable-next-line
  export let data;
  export let worldJSON;

  // Local variables
  let height = window.innerHeight;
  let width = window.innerWidth;
  let projection, path, context, rotation, defaultScale, canvasLength;
  let isDesktop = true;

  const borderColor = '#AAA';
  const landColor = '#777';
  const colorInt = d3.interpolateLab(landColor, '#F7717D');
  const colorInt2 = d3.interpolateLab(landColor, '#FAC675');
  const strokeInt = d3.interpolateLab(borderColor, '#FFF');
  const tilt = 5;

   /**
   * Read the map data.
   */
  onMount(() => {
    data.map_points = data.map_points.map(point => {
      if (!Array.isArray(point.place)) {
        point.place = [point.place];
      }
      if (!Array.isArray(point.place2)) {
        if (point.place2) {
          point.place2 = [point.place2];
        } else {
          point.place2 = [];
        }
      }
      if (!Array.isArray(point.focus) && point.focus) {
        point.focus = [point.focus];
      }
      return Object.assign({}, point, {
        geo: {
          type: 'FeatureCollection',
          features: point.place.map(place => worldJSON.features.find(feature => place === feature.properties.CNTRY_NAME))
        },
        geo2: {
          type: 'FeatureCollection',
          features: point.place2.map(place => worldJSON.features.find(feature => place === feature.properties.CNTRY_NAME))
        },
        focusPoint: (point.focus && point.focus.length > 0) ? {
          type: 'FeatureCollection',
          features: point.focus.map(place => worldJSON.features.find(feature => place === feature.properties.CNTRY_NAME))
        } : null
       });
    });

    // Generate scroll interpolation functions
    let p1 = [0, 0], r1 = [0, 0, 0];
    let p2 = d3.geoCentroid(data.map_points[0].focusPoint || data.map_points[0].geo);
    let r2 = [-p2[0], tilt - p2[1], 0];

    for (let i = 1; i < data.map_points.length; i++) {
      p1 = p2, p2 = d3.geoCentroid(data.map_points[i].focusPoint || data.map_points[i].geo);
      r1 = r2, r2 = [-p2[0], tilt - p2[1], 0];
      data.map_points[i-1].interpolateAngle = Versor.interpolateAngles(r1, r2);
    }

    initMap();
    drawMap(
      data.map_points[0].interpolateAngle(0),
      data.map_points[0].scale,
      [{json: data.map_points[0].geo, int: 1}],
      [{json: data.map_points[0].geo2, int: 1}],
      1
    );

    // Called whenever the page is resized or scrolled
    const drawMapFromScroll = () => {
      const top = (window.pageYOffset || document.documentElement.scrollTop)
        - (document.documentElement.clientTop || 0) - document.querySelector('.map-wrap').offsetTop;

      const currBreakpoint = Math.floor(top / height);

      if (currBreakpoint < 0) {
        drawMap(
          data.map_points[0].interpolateAngle(0),
          data.map_points[0].scale,
          [{json: data.map_points[0].geo, int: 1}],
          [{json: data.map_points[0].geo2, int: 1}],
          1
        );
        return;
      }

      const interpolateScale = i => {
        const y1 = parseFloat(data.map_points[currBreakpoint].scale);
        const y2 = parseFloat(data.map_points[currBreakpoint + 1].scale);
        return (((y2 - y1) * i) + y1);
      };

      let mapOpacity = 1;
      const minOpacity = 0.1;
      if (data.map_points[currBreakpoint] && data.map_points[currBreakpoint + 1]) {
        if (data.map_points[currBreakpoint].img && data.map_points[currBreakpoint + 1].img) {
          mapOpacity = minOpacity;
        } else if (!data.map_points[currBreakpoint].img && !data.map_points[currBreakpoint + 1].img) {
          mapOpacity = 1;
        } else if (data.map_points[currBreakpoint].img && !data.map_points[currBreakpoint + 1].img) {
          // Fading in
          mapOpacity = Math.max(2 * ((top / height) - currBreakpoint) - 0.5, minOpacity);
        } else if (!data.map_points[currBreakpoint].img && data.map_points[currBreakpoint + 1].img) {
          // Fading out
          mapOpacity = Math.max(1 - (((top / height) - currBreakpoint) * 2), minOpacity);
        }
      }

      if (data.map_points[currBreakpoint] && data.map_points[currBreakpoint].interpolateAngle) {

        // Calculate which countries to remain highlighted
        const remainHighlighted = {
          type: 'FeatureCollection',
          features: data.map_points[currBreakpoint].geo.features.filter(
            feature => data.map_points[currBreakpoint + 1].geo.features.find(
              nextFeature => nextFeature.properties.CNTRY_NAME === feature.properties.CNTRY_NAME
            )
          )
        };
        const remainHighlighted2 = {
          type: 'FeatureCollection',
          features: data.map_points[currBreakpoint].geo2.features.filter(
            feature => data.map_points[currBreakpoint + 1].geo2.features.find(
              nextFeature => nextFeature.properties.CNTRY_NAME === feature.properties.CNTRY_NAME
            )
          )
        };

        drawMap(
          data.map_points[currBreakpoint].interpolateAngle((top / height) - currBreakpoint),
          interpolateScale((top / height) - currBreakpoint),
          (JSON.stringify(data.map_points[currBreakpoint].geo) !== JSON.stringify(data.map_points[currBreakpoint + 1].geo)) ?
          [
            {json: data.map_points[currBreakpoint].geo, int: 1 - ((top / height) - currBreakpoint)},
            {json: data.map_points[currBreakpoint + 1].geo, int: (top / height) - currBreakpoint},
            {json: remainHighlighted, int: 1}
          ] : [{ json: data.map_points[currBreakpoint].geo, int: 1 }],
          (JSON.stringify(data.map_points[currBreakpoint].geo2) !== JSON.stringify(data.map_points[currBreakpoint + 1].geo2)) ?
          [
            {json: data.map_points[currBreakpoint].geo2, int: 1 - ((top / height) - currBreakpoint)},
            {json: data.map_points[currBreakpoint + 1].geo2, int: (top / height) - currBreakpoint},
            {json: remainHighlighted2, int: 1}
          ] : [{ json: data.map_points[currBreakpoint].geo2, int: 1 }],
          mapOpacity
        );
      }
    };

    // Add event listener for scrolling
    window.addEventListener('scroll', drawMapFromScroll);

    // Look for resize event
    window.addEventListener('resize', () => {
      initMap();
      drawMapFromScroll();
    });
  });

  const initMap = () => {
    height = window.innerHeight;
    width = window.innerWidth;

    // Get the device pixel ratio, falling back to 1.
    const dpr = window.devicePixelRatio || 1;

    canvasLength = height;
    let padding = 50;
    // Figure out if we are a mobile device
    isDesktop = (width / height >= 1.4);

    // Set up canvas
    d3.select('canvas#graphic-map')
      .style('height', `${canvasLength}px`)
      .style('width', `${canvasLength}px`);

    if (!isDesktop) {
      d3.select('canvas#graphic-map')
        .style('margin-left', `${(width - canvasLength) / 2}px`);
    }

    const canvas = document.querySelector(`canvas#graphic-map`);
    canvas.width = canvasLength * dpr;
    canvas.height = canvasLength * dpr;
    context = canvas.getContext('2d');
    context.scale(dpr, dpr);

    // Draw projection map
    projection = d3.geoOrthographic().fitExtent([[padding, padding], [canvasLength - padding, canvasLength - padding]], {type: 'Sphere'});
    defaultScale = projection.scale();
    path = d3.geoPath(projection, context);
  };

  const drawMap = (geoPosition, scale, specialFill, specialFill2, opacity = 1) => {
    projection.rotate(geoPosition);
    projection = projection.scale(defaultScale * scale);
    path = d3.geoPath(projection, context);

    d3.select('canvas#graphic-map')
      .style('opacity', opacity);

    // Canvas stuff
    context.clearRect(0, 0, canvasLength, canvasLength);
    context.beginPath(), path({type: 'Sphere'}), context.fillStyle = "#444", context.fill();
    context.beginPath(), path(worldJSON), context.fillStyle = landColor, context.fill();
    context.beginPath(), path(worldJSON), context.strokeStyle = borderColor, context.stroke();
    specialFill.forEach(fill => {
      context.beginPath(), path(fill.json), context.fillStyle = colorInt(fill.int), context.fill();
      context.beginPath(), path(fill.json), context.strokeStyle = strokeInt(fill.int), context.stroke();
    });
    specialFill2.forEach(fill => {
      context.beginPath(), path(fill.json), context.fillStyle = colorInt2(fill.int), context.fill();
      context.beginPath(), path(fill.json), context.strokeStyle = strokeInt(fill.int), context.stroke();
    });
  };
</script>

<style>
  .map-wrap {
    position: relative;
    background-color: #333;
  }

  .full-graphic {
    width: 100%;
    height: 100vh;
    display: flex;
    overflow: hidden;
    position: sticky;
    position: -webkit-sticky;
    top: 0;
  }

  .content-box {
    position: absolute;
    top: 0;
  }

  .point-box {
    height: 100%;
    padding: 50px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    white-space: pre-wrap;
    /* scroll-snap-align: start; */
  }

  .point-box.mobile {
    justify-content: flex-end;
    padding: 15px;
    max-width: 600px;
  }

  .point-box:last-of-type {
    margin-bottom: 0;
  }

  .point-image-wrap {
    display: flex;
    flex-direction: column;
    justify-content: center;
    position: absolute;
    padding: 20px;
  }

  .point-image-wrap.mobile {
    display: none;
  }

  .point-image {
    width: 100%;
  }

  .point-image-inner {
    display: none;
  }

  .point-image-inner.mobile {
    width: 100%;
    display: block;
  }

  .photo-credit {
    margin-top: 5px;
    color: #aaa;
    font-size: 12px;
    width: 100%;
    text-align: right;
  }

  .photo-credit.hide {
    display: none;
  }

  .links {
    margin-top: 15px;
  }

  .article-link {
    display: block;
    color: #009FDA;
  }

  h1 {
    margin-top: 30px;
    margin-bottom: 20px;
    font-size: 36px;
    line-height: 1.2em;
    font-weight: bold;
    color: white;
  }

  p {
    margin-top: 30px;
    color: white;
    font-size: 22px;
    line-height: 1.4em;
  }

  @media screen and (max-width: 600px) {
    h1 {
      margin-bottom: 10px;
    }

    p {
      font-size: 16px;
      margin-top: 10px;
    }
  }
</style>

<div class="map-wrap" style="height: {data.map_points.length * height}px">
  <div class="full-graphic" id="full-graphic">
    <canvas id="graphic-map" />
  </div>
  <div class="content-box">
    {#each data.map_points as point}
      {#if point.img}
        <div class="point-image-wrap {isDesktop ? '' : 'mobile'}" style="height: {height}px; width: {isDesktop ? canvasLength : width}px;">
          <img class="point-image" src="{point.img}" alt="{point.img_alt}">
          <div class="photo-credit">{point.img_credit}</div>
        </div>
      {/if}
      <div class="point-box {!isDesktop ? 'mobile' : ''}" style="height: {height}px; padding-left: {isDesktop ? height + 50 : 15}px;">
        <h1>{point.title}</h1>
        {#if point.img}
          <img class="point-image-inner {isDesktop ? '' : 'mobile'}" src="{point.img}" alt="{point.img_alt}">
          <div class="photo-credit {isDesktop ? 'hide' : ''}">{point.img_credit}</div>
        {/if}
        <p>{point.text}</p>
        <div class="links">
          {#if point.link_text1}
            <a class="article-link" target="_blank" href="{point.link_url1}">{point.link_text1}</a>
          {/if}
          {#if point.link_text2}
            <a class="article-link" target="_blank" href="{point.link_url2}">{point.link_text2}</a>
          {/if}
          {#if point.link_text3}
            <a class="article-link" target="_blank" href="{point.link_url3}">{point.link_text3}</a>
          {/if}
        </div>
      </div>
    {/each}
  </div>
</div>
